import { camelToSnake, snakeToCamel } from "utils/strings"
import _ from "lodash"

/**
 * @function
 * @name arrayToObject
 * @desc Map an array [] of items to an object {} list.
 *
 * @example
 *  import { arrayToObject } from "utils/helpers"
 *
 *  // returns {1: {id: 1, name: "Pico"}, 2: {id: 2, name: "Pica"}}
 *  arrayToObject(
 *    [{id: 1, name: "Pico"}, {id: 2, name: "Pica"}]
 *  )
 *
 * @param {Object[]} array - The array to convert to an object
 * @param {string} [key=id] - Optional, the key used for setting object keys
 *
 */
export const arrayToObject = (array, key = "id") =>
  array.reduce((obj, item) => {
    obj[item[key]] = item
    return obj
  }, {})

/**
 * @function
 * @name arrayToSentence
 * @desc Map an array [] of items to a correctly formed sentence structure.
 *
 * @example
 *  import { arrayToSentence } from "utils/helpers"
 *
 *  // returns "dog, cat and mouse"
 *  arrayToObject(["dog", "cat", "mouse"])
 *
 *  // returns "dog and cat"
 *  arrayToObject(["dog", "cat"])
 *
 *  // returns "dog"
 *  arrayToObject(["dog"])
 *
 * @param {Object[]} array - The array to convert to an object
 * @param {string} [separator=, ] - Optional, the separator is used to separate the array
 * @param {string} [lastSeparator= and ] - Optional, the lastSeparator is used to separate the last item in the array
 *
 */
export const arrayToSentence = (array, separator = ", ", lastSeparator = " and ") => {
  if (array.length === 0) {
    return ""
  }

  if (array.length === 1) {
    return array[0]
  }

  return `${array.slice(0, -1).join(separator)}${lastSeparator}${array[array.length - 1]}`
}

/**
 * @function
 * @name pluralize
 * @desc Return a singular or plural string based on a number.
 *
 * @example
 *  import { pluralize } from "utils/helpers"
 *
 *  // returns "3 dogs"
 *  pluralize("dog", "dogs", 3)
 *
 *  // returns "dog"
 *  pluralize("dog", "dogs", 1, false)
 *
 * @param {string} singular - Singular form of the string
 * @param {string} plural - Plural form of the string
 * @param {number} count - Number for selecting between singular or plural
 * @param {boolean} includeNumber - Include number in the return value
 *
 */
export const pluralize = (singular, plural, count, includeNumber = true, formatCount = true) => {
  const word = count !== 1 ? plural : singular
  return includeNumber ? `${formatCount ? count.toLocaleString() : count} ${word}` : word
}

/**
 * @function
 * @name truncate
 * @desc Return a truncated string based on a given length
 *
 * @example
 *  import { truncate } from "utils/helpers"
 *
 *  // returns "only a..."
 *  truncate("only a test", 6)
 *
 * @param {string} string - String to truncate
 * @param {string} length - Length of string to truncate to
 * @param {boolean} omission - String to indicate text omitted
 *
 */
export const truncate = (string, length = 0, omission = "...") => {
  return `${string.substring(0, length)}${string.length > length ? omission : ""}`
}

/**
 * @function
 * @name capitalize
 * @desc Returns a string with the first letter capitalized.
 *
 * @example
 *  import { capitalize } from "utils/helpers"
 *
 *  // returns "A test"
 *  capitalize("a test")
 *
 * @param {string} string - String to uppercase first character
 */
export const capitalize = string => string.charAt(0).toUpperCase() + string.slice(1)

/**
 * @function
 * @name isArray
 * @desc Returns true || false depending if value is an array.
 *
 * @example
 *  import { isArray } from "utils/helpers"
 *
 *  // returns true
 *  isArray([])
 *
 *  // returns false
 *  isArray({})
 *
 * @param {*} arr - Value to check if array
 *
 */
export const isArray = arr => Array.isArray(arr)

/**
 * @function
 * @name isObject
 * @desc Returns true || false depending if value is an object.
 *
 * @example
 *  import { isObject } from "utils/helpers"
 *
 *  // returns true
 *  isObject({})
 *
 *  // returns false
 *  isObject("")
 *
 * @param {*} obj - Value to check if object
 *
 */
export const isObject = obj => obj !== null && typeof obj === "object"

/**
 * @function
 * @name get
 * @desc Fetches a value from an object return a default if none found
 *
 * @example
 *  import { get } from "utils/helpers"
 *
 *  // returns "n/a"
 *  get({name: {first: "Pico", last: "das Robot"}}, "gender", "n/a")
 *
 *  // returns "robot"
 *  get({name: {first: "Pico", last: "das Robot"}, gender: "robot"}, "gender", "n/a")
 *
 *  // returns "Pico"
 *  get({name: {first: "Pico", last: "das Robot"}}, "name.first", "")
 *
 * @param {Object} object - The object to get value from
 * @param {string} stringPath - A string of the key to find in the object path.
 * @param {string} defaultValue - Default value returned, if none found.
 *
 */
export const get = (object, stringPath, defaultValue = null) => {
  const path = stringPath.split(".")

  const result = path.reduce(
    (res, key) => (res !== null && res !== undefined ? res[key] : res),
    object
  )

  return result === null || result === undefined || result === object ? defaultValue : result
}

/**
 * @function
 * @name ordinalNumber
 * @desc Returns the ordinalized value of the specified number.
 *
 * @example
 *  import { ordinalNumber } from "utils/helpers"
 *
 *  // returns "1st"
 *  ordinalNumber(1)
 *
 * @param {number} n - Number to ordinalize.
 *
 */
export const ordinalNumber = n => {
  const s = ["th", "st", "nd", "rd"]
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

/**
 * @function
 * @name isBlank
 * @desc Returns true || false depending if value is empty/blank.
 *
 * @example
 *  import { isBlank } from "utils/helpers"
 *
 *  // returns true
 *  isBlank([])
 *
 *  // returns false
 *  isBlank(["a", "b"])
 *
 * @param {*} value - Value to check if blank.
 * @param {string} key - Key to get if value is an object.
 *
 */
export const isBlank = (value, key = null) => {
  if (key) {
    return isBlank(get(value, key))
  }

  if (value?.constructor?.name == "Date") {
    return false
  }

  if (isArray(value)) {
    if (value.length) {
      return false
    }
  } else if (isObject(value)) {
    if (Object.keys(value).length) {
      return false
    }
  } else if (value === 0 || value === false) {
    return false
  } else if (value && value.toString().trim() !== "") {
    return false
  }

  return true
}

/**
 * @function
 * @name isPresent
 * @desc Opposite of isBlank, returns true || false depending if value is empty/blank.
 *
 * @example
 *  import { isPresent } from "utils/helpers"
 *
 *  // returns true
 *  isPresent(["a", "b"])
 *
 *  // returns false
 *  isPresent([])
 *
 * @param {*} value - Value to check if blank.
 * @param {string} key - Key to get if value is an object.
 *
 */
export const isPresent = (value, key = null) => !isBlank(value, key)

/**
 * @function
 * @name stringify
 * @desc Returns value as trimmed string.
 *
 * @example
 *  import { stringify } from "utils/helpers"
 *
 *  // returns "a test"
 *  stringify("    a test        ")
 *
 *  // returns ""
 *  stringify(null, "")
 *
 * @param {*} value - The value to check and stringify
 * @param {*} defaultValue - If value is blank, defaultValue is returned.
 *
 */
export const stringify = (value, defaultValue = "") => {
  return isBlank(value) ? defaultValue : value.toString().trim()
}

/**
 * @function
 * @name isEqual
 * @desc Checks if two strings are equal (trimmed and lower case).
 *
 * @example
 *  import { isEqual } from "utils/helpers"
 *
 *  // returns true
 *  isEqual("    AbC DEF      ", "abc def")
 *
 *  // returns true
 *  isEqual(123, "123  ")
 *
 *  // returns false
 *  isEqual("a", "b")
 *
 * @param {*} string1 - First value to compare against string2
 * @param {*} string2 - Second value to compare against string1
 *
 */
export const isEqual = (string1, string2) => {
  return stringify(string1).toLowerCase() === stringify(string2).toLowerCase()
}

export const cleanString = (string, regex = /[^a-zA-Z0-9 _-]/g) => {
  return string.replace(regex, "")
}

export const stringStartsWith = (string1, string2) =>
  stringify(string1)
    .toLowerCase()
    .startsWith(stringify(string2).toLowerCase())

export const stringIncludes = (string1, string2) =>
  stringify(string1)
    .toLowerCase()
    .includes(stringify(string2).toLowerCase())

/**
 * @function
 * @name unescape
 * @desc Unescape speficific unicode characters from a string.
 *
 * @example
 *  import { unescape } from "utils/helpers"
 *
 *  // returns Thomas O'Malley
 *  unescape("Thomas O&#39;Malley")
 *
 * @param {*} str - String to unescape specific unicode characters
 *
 */
export const unescape = str => {
  if (str === null) return

  const string = str.toString()
  const regexChars = /&(?:amp|lt|gt|quot|#39);/g
  const escapeCodes = {
    "&amp;": "&",
    "&lt;": "<",
    "&gt;": ">",
    "&quot;": '"',
    "&#39;": "'"
  }

  return string && regexChars.test(string)
    ? string.replace(regexChars, match => escapeCodes[match])
    : string
}

/**
 * @function
 * @name camelToSnakeKeys
 * @desc Convert object keys from camel to snake case
 *
 * @example
 *  import { camelToSnakeKeys } from "utils/helpers"
 *
 *  // returns { with_foo: true}
 *  camelToSnakeKeys({ withFoo: true })
 *
 * @param {*} obj - object with camel cased keys
 *
 */

export const camelToSnakeKeys = obj => {
  let newObj = {}
  let keys = Object.keys(obj)

  for (let i = 0; i < keys.length; i++) {
    newObj[camelToSnake(keys[i])] = obj[keys[i]]
  }

  return newObj
}

/**
 * @function
 * @name snakeToCamelKeys
 * @desc Convert object keys from snake to camel case
 *
 * @example
 *  import { snakeToCamelKeys } from "utils/helpers"
 *
 *  // returns { withFoo: true}
 *  snakeToCamelKeys({ with_foo: true })
 *
 * @param {*} obj - object with snake cased keys
 *
 */
export const snakeToCamelKeys = obj => transformKeys(snakeToCamel, obj)

// From: https://stackoverflow.com/a/50620653
const transformKeys = (transformFunc, obj) => {
  if (Array.isArray(obj)) {
    return obj.map(v => transformKeys(transformFunc, v))
  } else if (obj !== null && obj.constructor === Object) {
    return Object.keys(obj).reduce(
      (result, key) => ({
        ...result,
        [transformFunc(key)]: transformKeys(transformFunc, obj[key])
      }),
      {}
    )
  }
  return obj
}
